/**
 * KNK-1.11 — reading and changing Knock Mode for an account.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. What the answer means is `lib/knockMode.ts`; who may change
 * what is the database's (migration 20261005a1). Errors are thrown with the server's own
 * message, because every refusal here is a sentence written for the person reading it.
 */

import { supabase } from '@/lib/supabaseClient';
import { parseKnockModeStatus, type KnockAttestedKey, type KnockModeStatus } from '@/lib/knockMode';
import {
  parseActivationPreview,
  parseActivationRuns,
  type ActivationPreview,
  type ActivationRun,
} from '@/lib/knockActivation';

export async function fetchKnockModeStatus(): Promise<KnockModeStatus> {
  const { data, error } = await supabase.rpc('knock_mode_status');
  if (error) throw new Error(error.message);
  return parseKnockModeStatus(data);
}

/** field.settings.manage, own account only, and only while Knock Mode is on. Returns the new status. */
export async function setKnockReadinessItem(item: KnockAttestedKey, confirmed: boolean): Promise<KnockModeStatus> {
  const { data, error } = await supabase.rpc('set_knock_readiness_item', { p_item: item, p_confirmed: confirmed });
  if (error) throw new Error(error.message);
  return parseKnockModeStatus(data);
}

/** KNK-1.52. field.settings.manage, own account, Knock Mode on. A dry run: writes nothing. */
export async function previewKnockActivation(): Promise<ActivationPreview> {
  const { data, error } = await supabase.rpc('knock_activation_preview');
  if (error) throw new Error(error.message);
  const preview = parseActivationPreview(data);
  if (!preview) throw new Error('The preview came back in a shape this screen does not understand.');
  return preview;
}

/** Applies what was previewed. `expectedKnocks` is the previewed count; the server refuses a stale one. */
export async function applyKnockActivation(expectedKnocks: number): Promise<ActivationRun[]> {
  const { data, error } = await supabase.rpc('knock_activation_apply', { p_expected_knocks: expectedKnocks });
  if (error) throw new Error(error.message);
  return parseActivationRuns((data as { runs?: unknown } | null)?.runs);
}

export async function revertKnockActivation(activationId: string): Promise<ActivationRun[]> {
  const { data, error } = await supabase.rpc('knock_activation_revert', { p_activation_id: activationId });
  if (error) throw new Error(error.message);
  return parseActivationRuns((data as { runs?: unknown } | null)?.runs);
}

/** KNK-1.37. The current location policy, as reps read it on the phone. */
export interface LocationPolicyText {
  version: number;
  publishedOn: string | null;
  body: Record<string, unknown>;
}

export async function fetchLocationPolicy(): Promise<LocationPolicyText> {
  const { data, error } = await supabase.rpc('location_policy');
  if (error) throw new Error(error.message);
  const d = data as { version?: unknown; published_on?: unknown; body?: unknown } | null;
  if (!d || typeof d.version !== 'number' || typeof d.body !== 'object' || d.body === null) {
    throw new Error('The location policy came back in a shape this screen does not understand.');
  }
  return { version: d.version, publishedOn: typeof d.published_on === 'string' ? d.published_on : null, body: d.body as Record<string, unknown> };
}

/** KNK-1.37. field.settings.manage, own account, current version only. Returns the new status. */
export async function recordLocationPolicyReview(
  version: number, states: string[], reviewer: string, note: string | null,
): Promise<KnockModeStatus> {
  const { data, error } = await supabase.rpc('record_location_policy_review', {
    p_version: version, p_states: states, p_reviewer: reviewer, p_note: note,
  });
  if (error) throw new Error(error.message);
  return parseKnockModeStatus(data);
}
